/**
 * Копирование в буфер обмена, которое НЕ бросает.
 *
 * `navigator.clipboard.writeText` отказывает штатно и по причинам, на которые
 * страница не влияет: небезопасный контекст, отозванное разрешение, встроенный
 * webview и — то, что реально прилетело с боя (Hawk, 24.08.2026) —
 * `Failed to execute 'writeText' on 'Clipboard': Document is not focused`.
 * Последнее случается, когда к моменту записи фокус ушёл из документа: клик в
 * неактивном окне, открытые devtools, переключение вкладки посреди жеста.
 *
 * Отсюда две попытки. Clipboard API, а при его отказе — скрытая `textarea` с
 * `document.execCommand("copy")`: способ снят со стандарта, но он работает в
 * небезопасном контексте и в старых webview, где первого нет вовсе, и живёт
 * ровно один тик.
 *
 * Возвращает `false` вместо исключения: отказ копирования — ожидаемый исход
 * (правило `code-quality.md` §5), а решать, что показать человеку, обязан
 * вызывающий. Молча его глотать нельзя: невидимый отказ выглядит как
 * «скопировалось», и человек вставляет прошлое содержимое буфера.
 */
export async function copyText(value: string): Promise<boolean> {
	if (typeof navigator !== "undefined" && navigator.clipboard !== undefined) {
		try {
			await navigator.clipboard.writeText(value);
			return true;
		} catch {
			// Не отказ, а повод попробовать вторым способом.
		}
	}
	return copyViaTextarea(value);
}

/**
 * Фолбэк через выделение текста.
 *
 * Поле кладётся в поток вне видимости, но остаётся отображаемым: `display:none`
 * и `hidden` не дают выделения, без которого `execCommand("copy")` копирует
 * пустоту. `position: fixed` + нулевая прозрачность не двигают прокрутку.
 */
function copyViaTextarea(value: string): boolean {
	if (typeof document === "undefined") return false;

	const area = document.createElement("textarea");
	area.value = value;
	area.setAttribute("readonly", "");
	area.setAttribute("aria-hidden", "true");
	area.style.position = "fixed";
	area.style.top = "0";
	area.style.left = "0";
	area.style.width = "1px";
	area.style.height = "1px";
	area.style.opacity = "0";
	document.body.append(area);

	try {
		area.select();
		area.setSelectionRange(0, value.length);
		return document.execCommand("copy");
	} catch {
		return false;
	} finally {
		area.remove();
	}
}
